<template>
  <EBaseEntity v-bind="baseProps">
    <template #content-start>
      <EDetails
        :average-rating="tripadvisor.averageRating"
        :display-address="tripadvisor.displayAddress"
        :phone-numbers="tripadvisor.phoneNumbers"
        :review-count="tripadvisor.reviewCount"
        :source-url="tripadvisor.sourceURL"
        :opening-hours="openingHours"
        :url="tripadvisor.url"
      />
      <EHeading v-if="baseProps.description" as="h3" class="tripadvisor__heading">
        {{ $t('web.entities.description') }}
      </EHeading>
    </template>
    <template v-if="reviews && reviewsVisible" #content-end>
      <EHeading
        id="tripadvisor-reviews-heading"
        as="h3"
        class="tripadvisor__heading"
        data-test-id="tripadvisor-reviews-heading"
      >
        {{ $t('web.entities.tripadvisor.reviews.title.upper') }}
      </EHeading>
      <EReviews
        ref="reviews"
        :reviews="reviews"
        :source-url="tripadvisor.sourceURL"
        data-test-id="tripadvisor-reviews"
      />
    </template>
    <template v-if="reviews && !reviewsVisible" #footer>
      <div class="tripadvisor__footer" @click="showReviews">
        <div class="tripadvisor__reviews-toggle">
          <EButton
            class="tripadvisor__reviews-button"
            icon="chevron-down"
            variant="outline"
            icon-size="s"
            size="m"
            :aria-label="$t('web.entities.tripadvisor.reviews.toggle')"
          />
          <span aria-hidden="true">{{ $t('web.entities.tripadvisor.reviews.toggle') }}</span>
        </div>
      </div>
    </template>
  </EBaseEntity>
</template>

<script>
import { mapState } from 'vuex';

import { prefersReducedMotion, isDesktopUp } from '~~/common/js/client/screen';
import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import { localiseTime } from '~~/common/js/universal/datetime';
import { getPoiCategoryString } from '~/results/tripadvisor';

import EHeading from '~~/common/vue/components/heading';
import EButton from '~~/common/vue/components/button';
import EBaseEntity from '../base/index';
import EDetails from './details';
import EReviews from './reviews';

const MAX_REVIEWS = 3;

export default {
  name: 'ETripadvisorEntity',
  components: {
    EBaseEntity,
    EDetails,
    EHeading,
    EReviews,
    EButton,
  },
  props: {
    ...EBaseEntity.props,
    tripadvisor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      reviewsVisible: true,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    // these props replace base Entity's props with data from the Tripadvisor extension
    baseProps() {
      const { tripadvisor } = this;
      const tripadvisorLink = { title: 'Tripadvisor', url: tripadvisor.sourceURL || tripadvisor.url };
      // if primary image not found, fall back to the Entity image
      // python grpc always creates default messages instead of `null`
      // so we have to check for the actual field value
      const image = tripadvisor.image?.thumbnailUrl ? tripadvisor.image : this.image;

      return {
        ...this.$props,
        // strip away the `tripadvisor` prop
        tripadvisor: undefined,
        title: tripadvisor.name,
        subtitle: this.extendedSubtitle,
        image,
        imageUrl: tripadvisorLink.url,
        description: this.description || tripadvisor.description,
        moreUrl: this.description ? this.moreUrl : tripadvisor.sourceURL,
        ticketSubject: 'Tripadvisor Entity',
        sources: [...this.sources, tripadvisorLink],
        // if the main image is set, it tends to be repeated
        // as the first image of the `images` array
        gallery: tripadvisor.images?.slice(image ? 1 : 0),
        map: tripadvisor.location,
        moreImagesUrl: tripadvisor.viewPhotosURL,
        // ensures clicking on images in the gallery leads to the source URL
        // of that particular image on the TA website, rather than to the images vertical
        preferImageSourceUrl: true,
        widgetType: 'tripadvisor_entity',
        poweredByEcosia: this.sources.length === 0 && !this.license,
      };
    },
    extendedSubtitle() {
      return getPoiCategoryString(
        this.$t(`web.entities.tripadvisor.type.${this.tripadvisor.poiType.toLowerCase()}`),
        this.tripadvisor.categories ?? [],
      );
    },
    reviews() {
      const { reviews } = this.tripadvisor;
      if (reviews?.length > 0) {
        return reviews.slice(0, MAX_REVIEWS);
      }

      return null;
    },
    openingHours() {
      const { openingHoursWeek } = this.tripadvisor;
      // Due to Python's protobuf library not supporting nullable fields,
      // the way to check if openingHours is set is to see if the POI
      // is open at least one day of the week ¯\_(ツ)_/¯
      // see https://github.com/ecosia/core/pull/7739
      if (!openingHoursWeek ||
        !Object.values(openingHoursWeek).some(({ periods }) => periods.length > 0)) {
        return null;
      }

      return this.localiseOpeningHours(openingHoursWeek);
    },
  },
  mounted() {
    this.reviewsVisible = isDesktopUp();
  },
  methods: {
    localiseOpeningHours(openingHours) {
      const localiseEntry = ([name, day]) => {
        if (!day) {
          return [name, day];
        }

        const localisedPeriods = day.periods?.map(({ start, end }) => ({
          start: this.localiseOpeningHoursTime(start),
          end: this.localiseOpeningHoursTime(end),
        }));

        return [name, { periods: localisedPeriods }];
      };

      return Object.fromEntries(Object.entries(openingHours).map(localiseEntry));
    },
    localiseOpeningHoursTime(serialised) {
      const date = new Date(`01/01/1970 ${serialised}`);
      return localiseTime(this.locale, date);
    },
    showReviews() {
      this.reviewsVisible = true;
      const scrollOptions = {
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'center',
      };
      this.$nextTick(() => {
        const element = this.$refs.reviews?.$el;
        if (element) {
          element.scrollIntoView(scrollOptions);
          focusFocusableChild(element);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tripadvisor__heading {
  @extend %tripadvisor-heading-style;
}

.tripadvisor__footer {
  @include divider('top');

  cursor: pointer;
}

.tripadvisor__reviews-toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: $space-2s $space-l 0;
  font-size: $font-s;
  color: var(--color-text-secondary);
  background-color: var(--color-background-primary);
}

.tripadvisor__reviews-button {
  margin-top: -$space-1l;
  margin-bottom: $space-2s;
}
</style>
